import { Injectable, computed, inject, resource, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SecretsService } from './secrets.service';
import type { Secret, SecretSource } from '../../interfaces/secrets.interface';

/** The secrets and secret sources for one project, loaded together. */
interface SecretsBundle {
  secrets: Secret[];
  sources: SecretSource[];
}

/**
 * `resource()`-backed cache of a project's secrets and secret sources.
 *
 * Set the active project with {@link setProjectId}; the resource then loads
 * both `GET .../entries` and `GET .../sources` in parallel and re-fetches
 * whenever the project changes. Provided in `root`, so the secrets list, the
 * secret-sources list, and the add-connection dialog can share one cache keyed
 * by the (single) active project instead of each running its own loader.
 *
 * Follows the same signals/`resource()` shape as `ProjectsListStore`: the
 * single `firstValueFrom` below is the only bridge to the Observable-based
 * `SecretsService`; everything the components read is a signal.
 */
@Injectable({ providedIn: 'root' })
export class SecretsResourceStore {
  readonly #secretsService = inject(SecretsService);

  /** Active project id, or `null` until a consumer sets one. */
  readonly #projectId = signal<number | null>(null);

  readonly #resource = resource<SecretsBundle, number | undefined>({
    // Returning `undefined` (no project set) leaves the resource idle, so no
    // request is made until a consumer calls setProjectId(). The loader only
    // runs for a defined params, hence the non-null assertion.
    params: () => this.#projectId() ?? undefined,
    loader: ({ params }) => this.#load(params!),
  });

  /** Loaded secrets for the active project, or `[]` while idle/loading/errored. */
  readonly secrets = computed<Secret[]>(() =>
    this.#resource.hasValue() ? this.#resource.value().secrets : [],
  );

  /** Loaded secret sources for the active project, or `[]` while idle/loading/errored. */
  readonly secretSources = computed<SecretSource[]>(() =>
    this.#resource.hasValue() ? this.#resource.value().sources : [],
  );

  readonly isLoading = computed(() => this.#resource.isLoading());
  readonly hasError = computed(() => this.#resource.status() === 'error');

  /** The project the cache is currently keyed to (read-only view). */
  readonly projectId = this.#projectId.asReadonly();

  /**
   * Point the cache at a project. A non-positive or null id resets it to idle.
   * Setting the same id again is a no-op (the resource only re-runs when the id
   * changes); use {@link reload} to force a refetch.
   */
  setProjectId(projectId: number | null): void {
    this.#projectId.set(projectId && projectId > 0 ? projectId : null);
  }

  /** Force a refetch with the current project id (e.g. after a create/delete). */
  reload(): void {
    this.#resource.reload();
  }

  async #load(projectId: number): Promise<SecretsBundle> {
    const [secrets, sources] = await Promise.all([
      firstValueFrom(this.#secretsService.getSecrets(projectId)),
      firstValueFrom(this.#secretsService.getSecretSources(projectId)),
    ]);
    return { secrets, sources };
  }
}
